<template>
  <div
    v-for="(printArea, printAreaGroup) in printAreas"
    :key="printAreaGroup"
    :class="{ dragging: dragging }"
  >
    <h4>{{ $t(printArea.name) }}</h4>
    <div class="printarea-hotspots">
      <div
        v-for="hotspot in getFilteredHotspots(printArea.hotspots)"
        :key="hotspot"
        class="printarea-hotspot-item"
      >
        <div class="hotspot">
          <img
            v-if="displayHotspotImages"
            :src="HOTSPOTS[hotspot].image"
            :draggable="false"
          />
          <p>{{ $t(HOTSPOTS[hotspot].name) }}</p>
        </div>
        <div class="design-wrapper">
          <div
            v-if="hotspotConfigs[hotspot]"
            class="draggable-design"
            :draggable="true"
            @dragstart="onDragStart($event, hotspot)"
            @dragend="onDragEnd"
          >
            <img
              :src="
                compositionHelper.getDesignImage(
                  hotspotConfigs[hotspot][0].designId,
                  { height: 100, width: 100 },
                )
              "
              :draggable="false"
            />
            <div class="icon-wrapper">
              <Icon icon="drag" />
            </div>
          </div>
          <div v-else class="hotspot-placeholder" />
          <div
            v-if="
              dragging &&
              (!draggedMotive.designId || draggedMotive.hotspot !== hotspot)
            "
            class="hotspot-placeholder hotspot-overlay"
            :class="{
              'hotspot-overlay--target': dragTargetHotspot === hotspot,
            }"
            :draggable="false"
            @dragover.prevent="dragover(hotspot)"
            @dragleave.prevent="dragLeave()"
            @drop.prevent.stop="drop(printAreaGroup, hotspot)"
          >
            <div class="possible">
              {{
                hotspotConfigs[hotspot]
                  ? $t('DESIGNS.PRINTAREA.FEEDBACK.EXCHANGE')
                  : $t('DESIGNS.PRINTAREA.FEEDBACK.CHANGE')
              }}
            </div>
            <div class="impossible">
              {{ $t('DESIGNS.PRINTAREA.FEEDBACK.CANNOT_EXCHANGE') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { HotspotConfigs } from '@spreadgroup/common-partner-lib/composition';
import { HOTSPOTS, HotspotKey } from '@spreadgroup/common-partner-lib/states';
import { deepCopy } from 'commons/utils.ts';
import compositionHelper, {
  printAreas,
} from 'commons/composition/compositionHelper.ts';
import { narrowHotspotConfigsToProductType } from 'commons/composition/multiPrintAreaCreation';

const props = defineProps<{
  mainDesignId: number;
  hotspotConfigs: HotspotConfigs;
  hotspots?: HotspotKey[];
  displayHotspotImages?: boolean;
}>();

type DraggedMotive = {
  designId?: number;
  hotspot?: HotspotKey;
};

const draggedMotive = ref<DraggedMotive>({
  designId: undefined,
  hotspot: undefined,
});

const getPrintAreaDesignAndHotspot = (printAreaGroup: string) => {
  const hotspotConfig = Object.entries(props.hotspotConfigs).find(([key, _]) =>
    printAreas[printAreaGroup].hotspots.includes(key as HotspotKey),
  );

  if (hotspotConfig) {
    return {
      hotspot: hotspotConfig?.[0],
      design: hotspotConfig?.[1][0].designId,
    };
  }
};

const emit = defineEmits<{
  onHotspotConfigsSelection: [HotspotConfigs];
  onDefaultSelect: [HotspotKey];
}>();

const onHotspotSelection = (
  printAreaGroup: string,
  selectedHotspot: HotspotKey,
) => {
  const { hotspot, designId } = draggedMotive.value;

  if (!designId || !hotspot) return;

  const newHotspotConfigs = deepCopy(props.hotspotConfigs);

  const designToReplace = getPrintAreaDesignAndHotspot(printAreaGroup);
  if (designToReplace && designToReplace.design !== designId) {
    delete newHotspotConfigs[designToReplace.hotspot];
    newHotspotConfigs[hotspot] = [{ designId: designToReplace.design }];
  } else {
    delete newHotspotConfigs[hotspot];
  }

  newHotspotConfigs[selectedHotspot] = [{ designId }];

  // A print area holds at most one design, and this emit is the single place a change is produced.
  const legal = narrowHotspotConfigsToProductType({
    hotspotConfigs: newHotspotConfigs,
    supportedHotspots: props.hotspots,
  });

  emit('onHotspotConfigsSelection', legal);

  updateDefaultHotspot(legal);
};

const dragging = ref<boolean>(false);

const dragTargetHotspot = ref<HotspotKey | undefined>(undefined);

const dragover = (hotspot: HotspotKey) => {
  dragTargetHotspot.value = hotspot;
};

const dragLeave = () => {
  dragTargetHotspot.value = undefined;
};

const onDragStart = (event: DragEvent, hotspot: HotspotKey) => {
  event.dataTransfer?.setData('text/plain', hotspot);

  dragging.value = true;
  draggedMotive.value = {
    designId: props.hotspotConfigs[hotspot][0].designId,
    hotspot,
  };
};

const onDragEnd = () => {
  dragging.value = false;
};

const drop = (printAreaGroup: string, selectedHotspot: HotspotKey) => {
  onHotspotSelection(printAreaGroup, selectedHotspot);

  draggedMotive.value = {
    designId: undefined,
    hotspot: undefined,
  };
};

const updateDefaultHotspot = (newHotspotConfigs: HotspotConfigs) => {
  const mainDesignHotspot = Object.entries(newHotspotConfigs).find(
    ([, value]) =>
      (value as Array<{ designId: number }>).some(
        (config) => config.designId === props.mainDesignId,
      ),
  )?.[0];
  if (mainDesignHotspot) {
    emit('onDefaultSelect', mainDesignHotspot as HotspotKey);
  }
};

const getFilteredHotspots = (printAreaHotspots: readonly HotspotKey[]) => {
  if (!props.hotspots) return printAreaHotspots;
  return printAreaHotspots.filter((hotspot) =>
    props.hotspots!.includes(hotspot),
  );
};
</script>

<style scoped lang="scss">
@import 'src/scss/constants';
@import 'src/scss/styleguide/colors';

h4 {
  margin: 24px 0 8px 0;
}
.printarea-hotspots {
  border: 1px solid $grey10;
  border-radius: $border-radius-medium;
  display: flex;
  flex-wrap: wrap;
  padding: 24px;
  gap: 40px;
}

.printarea-hotspot-item {
  height: 140px;
  display: flex;
  gap: 16px;
}

.hotspot {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  gap: 8px;
  padding-block: 8px;
  min-width: 116px;
  text-align: center;

  img {
    flex: 1 1;
    min-height: 0;
    width: 88px;
    object-fit: contain;
  }

  p {
    margin: 0;
  }
}
.dragging .draggable-design {
  opacity: 0.5;
}
.design-wrapper {
  height: 140px;
  width: 200px;
  position: relative;

  .draggable-design {
    height: 100%;
    width: 100%;

    border: 1px solid $grey20;
    border-radius: $border-radius-medium;
    background-color: $grey10;
    display: flex;
    cursor: move;
    z-index: 1;

    img {
      flex: 1 1;
      margin: 8px;
      max-height: 124px;
      width: 124px;
      object-fit: contain;
      pointer-events: none;
    }

    .icon-wrapper {
      display: flex;
      flex: 1 1;
      justify-content: center;
      align-items: center;
      border-top-right-radius: $border-radius-medium;
      border-bottom-right-radius: $border-radius-medium;

      .icon {
        width: 28px;
        height: 28px;
      }
    }
  }

  .hotspot-placeholder {
    height: 100%;
    width: 100%;
    border: 1px dashed $grey10;
    border-radius: $border-radius-medium;
  }

  .hotspot-overlay {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #eef7ff;
    color: $pa-color-main;
    font-weight: bold;

    &--target {
      border-color: $pa-color-main;
    }

    .impossible {
      display: none;
    }
  }
}
</style>
